import { useState, useEffect, useMemo } from "react";
import { Loader2, Landmark, Lock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogFooter } from "./EditPercentagesDialog";
import {
    BANKING_COUNTRIES,
    DEFAULT_BANK_COUNTRY,
    digitsOnly,
    formatBranchDisplay,
    getBankingProfile,
} from "../lib/bankingLocale";

function resolveDefaultCountry(user, member) {
    if (member?.bank?.country) return member.bank.country;
    return user?.country || DEFAULT_BANK_COUNTRY;
}

export function BankAccountDialog({ pool, member, open, onClose, onSaved }) {
    const { user } = useAuth();
    const [country, setCountry] = useState(DEFAULT_BANK_COUNTRY);
    const [holderName, setHolderName] = useState("");
    const [bankName, setBankName] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [branchCode, setBranchCode] = useState("");
    const [busy, setBusy] = useState(false);

    const profile = useMemo(() => getBankingProfile(country), [country]);
    const hasBranch = Boolean(profile.branch);

    useEffect(() => {
        if (!open || !member) return;
        setCountry(resolveDefaultCountry(user, member));
        setHolderName(member?.bank?.holder_name || member?.name || "");
        setBankName(member?.bank?.bank_name || "");
        setAccountNumber("");
        setBranchCode("");
    }, [open, member, user?.country]);

    if (!open || !member) return null;

    const onCountryChange = (code) => {
        setCountry(code);
        setBranchCode("");
        setAccountNumber("");
    };

    const onBranchChange = (value) => {
        if (!profile.branch) {
            setBranchCode("");
            return;
        }
        const raw = digitsOnly(value);
        const capped = raw.slice(0, profile.branch.digitsMax);
        setBranchCode(formatBranchDisplay(profile, capped));
    };

    const onAccountChange = (value) => {
        const raw = profile.code === "EU"
            ? value.replace(/\s/g, "").toUpperCase().slice(0, profile.accountDigitsMax)
            : digitsOnly(value).slice(0, profile.accountDigitsMax);
        setAccountNumber(raw);
    };

    const submit = async (e) => {
        e.preventDefault();
        const acct = profile.code === "EU" ? accountNumber.replace(/\s/g, "") : digitsOnly(accountNumber);
        const branch = digitsOnly(branchCode);
        if (hasBranch && branch.length < profile.branch.digitsMin) {
            toast.error(`${profile.branch.label} is required (${profile.branch.digitsMin} digits)`);
            return;
        }
        if (acct.length < profile.accountDigitsMin) {
            toast.error(`${profile.accountLabel} must be at least ${profile.accountDigitsMin} digits`);
            return;
        }
        try {
            setBusy(true);
            const { data } = await api.put(`/pools/${pool.id}/members/${member.id}/bank`, {
                country: profile.code,
                holder_name: holderName,
                bank_name: bankName,
                account_number: acct,
                branch_code: branch,
            });
            toast.success("Bank account linked", {
                description: `····${data.pool.members.find((m) => m.id === member.id)?.bank?.account_last4}`,
            });
            onSaved?.(data.pool);
            onClose();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            onClose={onClose}
            title="Link bank account"
            subtitle={profile.dialogSubtitle}
            testid="bank-dialog"
        >
            <form onSubmit={submit} className="space-y-3" data-testid="bank-form">
                <div>
                    <label className="label-eyebrow">Country / region</label>
                    <select
                        value={country}
                        onChange={(e) => onCountryChange(e.target.value)}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid="bank-country"
                    >
                        {BANKING_COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow">Account holder name</label>
                    <input
                        value={holderName}
                        onChange={(e) => setHolderName(e.target.value)}
                        required
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid="bank-holder-name"
                    />
                </div>
                <div>
                    <label className="label-eyebrow">Bank name</label>
                    <input
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder={profile.bankNamePlaceholder}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid="bank-name"
                    />
                </div>
                <div className={hasBranch ? "grid grid-cols-2 gap-3" : ""}>
                    {hasBranch && (
                        <div>
                            <label className="label-eyebrow">{profile.branch.label}</label>
                            <input
                                value={branchCode}
                                onChange={(e) => onBranchChange(e.target.value)}
                                required
                                placeholder={profile.branch.placeholder}
                                inputMode="numeric"
                                className="input-dark mt-1.5 w-full rounded-md px-3 py-2 font-mono text-sm"
                                data-testid="bank-branch"
                            />
                        </div>
                    )}
                    <div className={hasBranch ? "" : "w-full"}>
                        <label className="label-eyebrow">{profile.accountLabel}</label>
                        <input
                            value={accountNumber}
                            onChange={(e) => onAccountChange(e.target.value)}
                            required
                            inputMode={profile.code === "EU" ? "text" : "numeric"}
                            className="input-dark mt-1.5 w-full rounded-md px-3 py-2 font-mono text-sm"
                            data-testid="bank-account"
                        />
                    </div>
                </div>
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.025] p-3 flex items-start gap-2.5">
                    <Lock className="h-4 w-4 text-[#00BF0D] mt-0.5 shrink-0" />
                    <p className="text-xs text-white/55">{profile.payoutNote}</p>
                </div>
                <DialogFooter>
                    <button type="button" onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                    <button
                        type="submit"
                        disabled={busy}
                        className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
                        data-testid="bank-submit"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />}
                        {busy ? "Linking…" : "Link account"}
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}
